// @ts-strict-ignore
import * as Sentry from '@sentry/browser';
import debug from 'debug';

import { App } from 'config/setup';
import { DebugLogsNamespace } from 'constants/debug-logs-namespace';
import { ServerError } from 'constants/server-error';
import { trackGTMEvent } from 'helpers/analytics';
import { delay } from 'helpers/delay';
import { redirectToAccounts } from 'helpers/redirect-to-accounts';
import { redirectToLicenseExpired, redirectToStartupError, redirectToSubscription } from 'helpers/routing';
import { AppStateProvider } from 'services/app-state-provider';
import ChatWidget from 'services/chat-widget';
import { DEFAULT_RECONNECT_RETRY_COUNT } from 'services/connectivity/reconnector/events';
import { getReconnector } from 'services/connectivity/reconnector/service';
import { registerStartupReauthAttempt } from 'services/server/startup-reauth-guard';
import { endSession } from 'services/session';
import { getCanManageSubscription } from 'store/features/session/selectors';
import { StartupErrorActions } from 'store/views/startup-error/actions';

import { logout } from './logout';
import { isWebsocketOpen } from './websocket-connection';

const log = debug(DebugLogsNamespace.AppServerConnection);

interface HandleServerErrorOptions {
  error: ServerError;
  errorObject: unknown;
  isStartup: boolean; // used to distinguish between startup process and reconnections
}

export async function handleServerError(options: HandleServerErrorOptions): Promise<void> {
  const { error, errorObject, isStartup } = options;

  log(`Handling server error: ${error}`, errorObject);

  const reconnector = getReconnector();
  reconnector.stop();
  reconnector.loginFailed();

  if (error === ServerError.InvalidSession) {
    // Don't redirect to accounts if manual logout is in progress
    // The redirect is taking place in the other mechanism (App.destroy)
    if (!App.manualLogout) {
      // Break the app <-> accounts redirect loop: if startup keeps failing authorization,
      // accounts will just hand back another failing token forever. After a few attempts,
      // show the startup-error page instead of redirecting again. See AC-3814.
      if (isStartup && !registerStartupReauthAttempt()) {
        log('Repeated startup auth failures, showing startup error instead of redirecting to accounts');
        redirectToStartupError(errorObject);

        return;
      }

      log('Invalid session, redirecting to accounts');
      endSession();
      redirectToAccounts();
    }

    return;
  }

  if (error === ServerError.TooManyRequests || error === ServerError.MisdirectedRequest) {
    log(`Request needs retrying, reconnecting...`);

    const initialDelay = 1000 + Math.random() * 3000;

    // A rare case in which we got too_many_requests during startup
    if (isStartup) {
      log('Cannot use reconnector, refreshing the page instead');
      await delay(initialDelay);
      window.location.reload();

      return;
    }

    const reconnector = getReconnector();
    void reconnector.reconnect({
      reason: 'too_many_requests',
      initialDelay,
      maxAttempts: DEFAULT_RECONNECT_RETRY_COUNT,
    });

    return;
  }

  AppStateProvider.dispatch(StartupErrorActions.setSignInError({ signInError: error }));

  if (error === ServerError.UnexpectedError) {
    Sentry.addBreadcrumb({
      type: 'debug',
      category: 'startup-error',
      message: `Handling unexpected server error: ${JSON.stringify(errorObject)}`,
    });

    redirectToStartupError(errorObject);

    return;
  }

  ChatWidget.updateCustomerDetails();

  trackGTMEvent('VirtualPageview', {
    virtualPageURL: document.location.pathname,
    virtualPageTitle: document.title,
  });

  if (error === ServerError.LicenseBlockedInCRM) {
    log('License blocked in CRM, redirecting to error page');
    App.visitInfoPage('/license-inaccessible');

    return;
  }

  if (error === ServerError.IpNotAllowed) {
    log(`IP not allowed, redirecting to error page`);
    App.visitInfoPage('/ip-not-allowed');

    return;
  }

  if (error === ServerError.AccountSuspended) {
    log(`Account suspended, redirecting to error page`);
    App.visitInfoPage('/suspended');

    return;
  }

  if (error === ServerError.WaitingForApproval) {
    log(`Waiting for approval, redirecting to error page`);
    App.visitInfoPage('/waiting-for-approval');

    return;
  }

  const isSubscriptionPath = /\/subscription$/.test(window.location.pathname);

  if (error === ServerError.TooManyAgents && !isSubscriptionPath) {
    log(`Too many agents, redirecting to error page`);
    App.visitInfoPage('/too-many-agents');

    return;
  }

  if (error === ServerError.LicenseExpired) {
    if (isWebsocketOpen()) {
      log('Logging out from websocket');
      await logout();
      log('Logged out successfully');
    }
  }

  const canManageSubscription = AppStateProvider.selectFromStore(getCanManageSubscription);

  if (!canManageSubscription) {
    log(`Redirecting to license expired`);
    redirectToLicenseExpired();

    return;
  }

  AppStateProvider.dispatch({ type: 'APP_ERROR' });

  log(`Redirecting to subscription`);
  redirectToSubscription();
}
